import React, { useEffect, useState, useCallback } from "react";
import {
  CrossmintProvider,
  CrossmintCheckoutProvider,
  CrossmintHostedCheckout,
  useCrossmintCheckout,
} from "@crossmint/client-sdk-react-ui";
import logo_violet from "@assets/images/logo_violet.svg";
import constant from "@utils/constant"

import metamask_logo from "@assets/images/metamask_logo.png";
import crossmint from "@assets/images/crossmint.svg";
import core from "@assets/images/core.png";
import { useAppContext } from "@context/AppContext";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "@hooks/useApiClient";
import { WalletActionTypes } from "../../context/WalletContext";

function CheckoutWithTracking({ mintData, price, onError }) {
  const { get } = useApiClient();
  const { siteDetails, refreshNftData } = useAppContext();
  const { order } = useCrossmintCheckout();
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);

  const setAddress = (walletAddress) => {
    dispatch({
        type: WalletActionTypes.SET_CONNECTION_STATE,
        wallet: {
          address: walletAddress,
          isConnected: true,
        }
      });
  }

  // Memoize the completion handler to prevent dependency issues
  const handleOrderCompletion = useCallback((order) => {
    try {
      // Set the wallet address from the order
      if (order.lineItems?.[0]?.delivery?.recipient?.walletAddress) {
        const walletAddress = order.lineItems[0].delivery.recipient.walletAddress;
        console.log("Setting wallet address:", walletAddress);
        setAddress(walletAddress);
        
        // Wait 10 seconds, then refresh NFT data and redirect
        console.log("Waiting 10 seconds before refreshing NFT data...");
        setTimeout(() => {
          console.log("Refreshing NFT data after 10 seconds...");
          refreshNftData();
          
          // Navigate to my page after refresh
          console.log("Crossmint order completed, navigating to /my");
          navigate("/my");
        }, 10000); // 10 seconds delay
      }
    } catch (error) {
      console.error("Error processing completed order:", error);
      // Still navigate even if there's an error, but without delay
      navigate("/my");
    }
  }, [navigate, refreshNftData, setAddress]);

  useEffect(() => {
    if (order?.phase === "completed") {
      handleOrderCompletion(order);
    }
    
    // Reset showCheckout when order is cancelled or failed
    if (order?.phase === "cancelled" || order?.phase === "failed") {
      console.log("Order cancelled or failed, resetting checkout");
      setShowCheckout(false);
    }
  }, [order, handleOrderCompletion]);

  const validateNextName = async () => {
    const nameAvailable = await get(`/artCreationPack/${siteDetails.artCreationPackId}/approvednames`);
    const nextNameNumber = nameAvailable.find(
      (name) => name.approvedName === mintData.name.approvedName
    )?.nextNameNumber;
    return mintData.name.number >= nextNameNumber ? true : false;
  };

  const handleCheckoutClick = async () => {
    // Reset showCheckout first to ensure clean state
    setShowCheckout(false);
    
    const isValid = await validateNextName();
    if (!isValid) {
      onError();
      return;
    }
    
    // Show the hidden CrossmintHostedCheckout and simulate a click
    // Use setTimeout to ensure state update has propagated
    setTimeout(() => {
      setShowCheckout(true);
    }, 50);
  };

  useEffect(() => {
    if (showCheckout) {
      const interval = setInterval(() => {
        const crossmintButton = document.querySelector(".CrossmintHostedCheckoutButton");
        if (crossmintButton) {
          crossmintButton.click(); // Auto click
          clearInterval(interval); // Clean up
        }
      }, 100); // Retry every 100ms until it exists
      return () => clearInterval(interval);
    }
  }, [showCheckout]);

  return (
    <>
      <div className="mintButton" onClick={handleCheckoutClick}>
        <img src={crossmint} className="crossmint_logo" alt="logo" />
        <span className="mintButtonText">Pay with Card</span>
      </div>
      {showCheckout && (
        <div style={{ display: "none" }}>
          <CrossmintHostedCheckout
            appearance={{
              theme: {
                button: "light",
              },
            }}
            lineItems={{
              collectionLocator: constant.CROSSMINT_COLLECTION_LOCATOR,
              callData: {
                totalPrice: price,
                quantity: 1,
                _mintObj: mintData,
              },
            }}
            payment={{
              crypto: { enabled: true },
              fiat: { enabled: true },
            }}
          />
        </div>
      )}
    </>
  );
}

const MintPopup = ({ mintData, price, onWalletClick, onClose, onError }) => {
  const { themeConfig } = useAppContext();

  return (
    <div className="mintPopup">
      <div className="close-btn__wrapper" onClick={onClose}>
        <div className="close-btn__bar bar1"></div>
        <div className="close-btn__bar bar2"></div>
      </div>
      <div className="popup-info">
        <img className="mint-popup__logo" src={logo_violet} alt="logo" />
        <h3 className="mint-popup__title">Buy New {themeConfig.APP_NFT_NAME}</h3>
      </div>
      <div className="mint-button-group">
        <div className="mintButton" onClick={onWalletClick}>
          <img src={core} className="metamask_logo" alt="logo" />
          <span className="mintButtonText">Core Wallet</span>
        </div>
        <div className="mintButton" onClick={onWalletClick}>
          <img src={metamask_logo} className="metamask_logo" alt="logo"/>
          <span className="mintButtonText">Metamask</span>
        </div>
        {import.meta.env.VITE_CROSSMINT || import.meta.env.VITE_CROSSMINT === "true" ? (
          <CrossmintProvider apiKey={constant.CROSSMINT_API}>
            <CrossmintCheckoutProvider>
              <CheckoutWithTracking mintData={mintData} price={price} onError={onError} />
            </CrossmintCheckoutProvider>
          </CrossmintProvider>
        ) : null}
      </div>
    </div>
  );
};

export default MintPopup;
